/**
 * Multi-step wizard form component generator
 */

import {
  getAllFormSchemaFields,
  getStepFieldNames
} from '@core-engine/schema/field-helpers/index.js'
import type { FormSchema, GeneratorOptions } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import { toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import {
  buildDefaultValues,
  buildFieldArrayDecls,
  buildWatchDecls,
  buildZodFields,
  escapeJsx,
  getListFields,
  hasFormDescriptionFields,
  hasSelectFields,
  renderFieldsJSX,
  resolveUiImport,
  type StepResult
} from '@next-adapter/generators/form-pipeline/form-component-shared.js'

export function generateMultiStepForm(
  schema: FormSchema,
  cwd: string,
  adminDir: string,
  options: GeneratorOptions
): StepResult {
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const kebab = toKebabCase(formName)
  const actionImportPath = resolveSchemaActionImportPath(formName, options.outputNamespace)
  const steps = schema.steps!

  // All fields flattened (for Zod schema + defaults)
  const allFields = getAllFormSchemaFields(schema)
  const zodFields = buildZodFields(allFields)
  const defaults = buildDefaultValues(allFields)

  // List fields and watch declarations are global
  const listFields = getListFields(allFields)
  const hasListFields = listFields.length > 0
  const { setup: watchSetup } = buildWatchDecls(allFields)

  const rhfImport = hasListFields
    ? `import { useFieldArray, useForm } from 'react-hook-form'`
    : `import { useForm } from 'react-hook-form'`
  const fieldArraySetup = hasListFields ? `\n${buildFieldArrayDecls(listFields)}\n` : ''

  // Build STEPS constant
  const stepsConst = buildStepsConstant(steps)

  // Build per-step content blocks
  const stepContentBlocks = steps
    .map((step, index) => {
      const fieldsJSX = renderFieldsJSX(step.fields)
      return `          {currentStep === ${index} && (\n            <>\n${fieldsJSX}\n            </>\n          )}`
    })
    .join('\n')

  const submitText = schema.submitButtonText || 'Submit'
  const successMessage = escapeJsx(schema.successMessage || 'Form submitted successfully!')
  const redirectUrl = schema.redirectUrl
  const successHandler = redirectUrl
    ? `window.location.href = ${JSON.stringify(redirectUrl)}`
    : 'setSubmitted(true)'

  // Resolve UI imports
  const hasRadio = allFields.some((f) => f.type === 'radio')
  const hasFileUpload = allFields.some((f) => f.type === 'file' || f.type === 'upload')
  const hasFormDescriptions = hasFormDescriptionFields(allFields)
  const hasSelectControls = hasSelectFields(allFields)
  const buttonImport = resolveUiImport(cwd, 'button')
  const formImport = resolveUiImport(cwd, 'form')
  const inputImport = resolveUiImport(cwd, 'input')
  const textareaImport = resolveUiImport(cwd, 'textarea')
  const selectImport = hasSelectControls ? resolveUiImport(cwd, 'select') : ''
  const radioGroupImport = resolveUiImport(cwd, 'radio-group')
  const mediaUploadImport = resolveUiImport(cwd, 'media-field')

  const content = buildComponentSource({
    pascal,
    kebab,
    rhfImport,
    hasRadio,
    hasFileUpload,
    hasListFields,
    hasFormDescriptions,
    hasSelectControls,
    actionImportPath,
    buttonImport,
    formImport,
    inputImport,
    textareaImport,
    selectImport,
    radioGroupImport,
    mediaUploadImport,
    zodFields,
    defaults,
    stepsConst,
    fieldArraySetup,
    watchSetup,
    stepContentBlocks,
    submitText,
    successMessage,
    successHandler
  })

  return {
    files: [createGeneratedFile(`${adminDir}/components/forms/${kebab}-form.tsx`, content)]
  }
}

function buildStepsConstant(steps: NonNullable<FormSchema['steps']>): string {
  const entries = steps.map((step) => {
    const fieldNames = getStepFieldNames(step)
    const fieldsStr = fieldNames.map((n) => `'${n}'`).join(', ')
    const desc = step.description ? `, description: '${escapeQuotes(step.description)}'` : ''
    return `  { name: '${step.name}', label: '${escapeQuotes(step.label)}'${desc}, fields: [${fieldsStr}] }`
  })
  return `const STEPS = [\n${entries.join(',\n')}\n]`
}

function escapeQuotes(str: string): string {
  return str.replace(/'/g, "\\'")
}

interface ComponentParts {
  pascal: string
  kebab: string
  actionImportPath: string
  rhfImport: string
  hasRadio: boolean
  hasFileUpload: boolean
  hasListFields: boolean
  hasFormDescriptions: boolean
  hasSelectControls: boolean
  buttonImport: string
  formImport: string
  inputImport: string
  textareaImport: string
  selectImport: string
  radioGroupImport: string
  mediaUploadImport: string
  zodFields: string
  defaults: string
  stepsConst: string
  fieldArraySetup: string
  watchSetup: string
  stepContentBlocks: string
  submitText: string
  successMessage: string
  successHandler: string
}

function buildComponentSource(p: ComponentParts): string {
  const queryClientImport = p.hasFileUpload
    ? `\nimport { QueryClient, QueryClientProvider } from '@tanstack/react-query'`
    : ''
  const queryClientSetup = p.hasFileUpload ? `\nconst queryClient = new QueryClient()\n` : ''
  const providerOpen = p.hasFileUpload
    ? `    <QueryClientProvider client={queryClient}>
      <React.Suspense fallback={null}>
        <${p.pascal}FormInner />
      </React.Suspense>
    </QueryClientProvider>`
    : `    <React.Suspense fallback={null}>
      <${p.pascal}FormInner />
    </React.Suspense>`
  const exportWrapper = `\nexport function ${p.pascal}Form() {
  return (
${providerOpen}
  )
}\n`

  return `'use client'

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { ChevronLeft, ChevronRight${p.hasListFields ? ', Trash2' : ''} } from 'lucide-react'
import { createParser, useQueryState } from 'nuqs'
import * as React from 'react'
${p.rhfImport}
import { z } from 'zod/v3'${queryClientImport}
import { create${p.pascal}Submission } from '@admin/actions/${p.actionImportPath}'
import { Button } from '${p.buttonImport}'
import {
  Form,
  FormControl,
${p.hasFormDescriptions ? '  FormDescription,\n' : ''}  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '${p.formImport}'
import { Input } from '${p.inputImport}'${p.hasFileUpload ? `\nimport { MediaField } from '${p.mediaUploadImport}'` : ''}
${p.hasRadio ? `import { RadioGroup, RadioGroupItem } from '${p.radioGroupImport}'\n` : ''}
import { Textarea } from '${p.textareaImport}'${
    p.hasSelectControls
      ? `
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '${p.selectImport}'
`
      : ''
  }

const formSchema = z.object({
${p.zodFields}
})

type FormValues = z.infer<typeof formSchema>
${queryClientSetup}
${p.stepsConst}

const stepParser = createParser({
  parse(value) {
    if (!/^\\d+$/.test(value)) {
      return null
    }
    const step = Number.parseInt(value, 10)
    if (!Number.isInteger(step) || step < 0 || step >= STEPS.length) {
      return null
    }
    return step
  },
  serialize(value) {
    return String(value)
  }
}).withDefault(0)

function ${p.pascal}FormInner() {
  const [currentStep, setCurrentStep] = useQueryState('step', stepParser)
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, startSubmitTransition] = React.useTransition()

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
${p.defaults}
    },
  })

${p.fieldArraySetup}${p.watchSetup}
  async function handleNext() {
    const stepFields = STEPS[currentStep].fields as (keyof FormValues)[]
    const isValid = await form.trigger(stepFields, { shouldFocus: true })
    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  function handleBack() {
    setCurrentStep(currentStep - 1)
  }

  function onSubmit(values: FormValues) {
    startSubmitTransition(async () => {
      try {
        const result = await create${p.pascal}Submission(values)
        if (result.success) {
          ${p.successHandler}
        } else {
          form.setError('root', { message: result.error || 'Something went wrong' })
        }
      } catch {
        form.setError('root', { message: 'Something went wrong. Please try again.' })
      }
    })
  }

  if (submitted) {
    return (
      <div className="rounded-lg border p-6 text-center">
        <h3 className="text-lg font-semibold">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">${p.successMessage}</p>
      </div>
    )
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
        {/* Step header */}
        <div>
          <h3 className="text-lg font-semibold">{STEPS[currentStep].label}</h3>
          {'description' in STEPS[currentStep] && STEPS[currentStep].description && (
            <p className="text-sm text-muted-foreground mt-1">{STEPS[currentStep].description}</p>
          )}
        </div>

        {/* Step content */}
        <div key={currentStep} className="animate-in fade-in duration-300 space-y-6">
${p.stepContentBlocks}
        </div>

        {form.formState.errors.root && (
          <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
        )}

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="size-4" />
            Back
          </Button>
          {currentStep < STEPS.length - 1 ? (
            <Button type="button" onClick={handleNext}>
              Next
              <ChevronRight className="ml-2 size-4" />
            </Button>
          ) : (
            <Button type="button" disabled={submitting} onClick={() => form.handleSubmit(onSubmit)()}>
              {submitting ? 'Submitting...' : '${p.submitText}'}
            </Button>
          )}
        </div>
      </form>
    </Form>
  )
}
${exportWrapper}`
}
